import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { useSEO, BASE } from "@/lib/seo";
import { METIERS } from "@/lib/partenaires";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";
import NotFound from "@/pages/NotFound";

const HYDRO_IMAGES = [
  { src: "/images/hydrographie-peinture-decor-1.webp", alt: "Objet décoré par hydrographie peinture, Hydrographie Concept" },
  { src: "/images/hydrographie-peinture-decor-2.webp", alt: "Impression hydrographique sur support, Hydrographie Concept" },
  { src: "/images/hydrographie-peinture-decor-3.webp", alt: "Décor hydrographique réalisé par Hydrographie Concept" },
];
const FONDERIE_IMAGES = [
  { src: "/images/fdp-personnage.webp", alt: "sculpture bronze fonderie du Péroux Vonnas" },
  { src: "/images/fdp-plante.webp", alt: "sculpture bronze fonderie du Péroux à Vonnas" },
];
const PETITBARAT_IMAGES = [
  { src: "/images/petitbarat-singe.webp", alt: "atelier polyester Petit Barat Montaigut en Combraille" },
  { src: "/images/petitbarat-spheres.webp", alt: "atelier polyester sculpture résine, Petit Barat Montaigut en Combraille" },
  { src: "/images/petitbarat-gorille.webp", alt: "sculpture atelier polyester Petit Barat Montaigut en Combraille" },
];
const EVOLUDIS_IMAGES = [
  { src: "/images/evoludis-cheval-entier.webp", alt: "Sculpture cavalier d'échecs monumental en résine, Evoludis Concept" },
  { src: "/images/sculpture-pop-resine-chromee-rose.jpg", alt: "Sculpture pop en résine, finition rose chromée — moulage et finition par Evoludis Concept" },
];
const FERRONNERIE_IMAGES = [
  { src: "/images/arbre-sculptural-metal-forge.jpg", alt: "Arbre sculptural en métal forgé, réalisé par l'atelier Art de Forge, installé sur un rond-point urbain" },
];

const FICHES = {
  "hydrographie-concept": {
    metier: "finitions",
    title: "Hydrographie peinture et impression hydrographique | Sculpteur Français",
    description:
      "Hydrographie peinture et impression hydrographique sur tous supports : Hydrographie Concept, à Saint-Just-Saint-Rambert, propose environ 6 000 motifs, sur mesure.",
    headline: "Hydrographie Concept, hydrographie peinture et impression hydrographique",
    titleLines: ["Hydrographie Concept,", "hydrographie peinture et", "impression hydrographique"],
    lead: "Hydrographie Concept est spécialisée dans l'hydrographie, que l'on appelle aussi impression hydrographique ou hydrodipping. Le principe : un film imprimé est déposé à la surface de l'eau, l'objet y est plongé, et le motif se transfère en épousant chaque courbe. L'atelier est installé à Saint-Just-Saint-Rambert, dans la Loire.",
    image: {
      src: "/images/hydrographie-concept-logo.webp",
      alt: "Logo Hydrographie Concept, impression hydrographique sur tout support",
      caption: "Hydrographie Concept — impression hydrographique sur tout support",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Finitions et décors de surface",
    organization: {
      "@type": "Organization",
      name: "Hydrographie Concept",
      url: "https://www.hydrographie-concept.fr",
      address: { "@type": "PostalAddress", addressLocality: "Saint-Just-Saint-Rambert", addressRegion: "Loire", addressCountry: "FR" },
    },
    ogTitle: "Hydrographie peinture et impression hydrographique",
    sections: [
      {
        h2: "Un atelier d'hydrographie pour tous les supports",
        paragraphs: [
          "Hydrographie Concept dispose d'environ 6 000 motifs et intervient sur tout type de support. L'atelier personnalise des objets et des surfaces, réalise des projets uniques et sur mesure, et redonne vie à des objets anciens par l'upcycling.",
          "Il accompagne aussi bien les particuliers que les professionnels, en France comme à l'international.",
        ],
      },
      {
        h2: "L'hydrographie au service de la sculpture",
        paragraphs: [
          "Pour un sculpteur, l'hydrographie ouvre une piste rare : habiller un volume d'un décor continu, qui suit le relief sans raccord visible, là où le pinceau trouve vite ses limites.",
        ],
      },
    ],
    contactH2: "Contacter Hydrographie Concept",
    city: "Saint-Just-Saint-Rambert (Loire)",
    siteUrl: "https://www.hydrographie-concept.fr",
    siteLabel: "www.hydrographie-concept.fr",
    images: HYDRO_IMAGES,
  },
  "fonderie-du-peroux": {
    metier: "fonderie-art",
    title: "Fonderie d’art, bronze, moules, coulage, patine, coulage | Sculpteur Français",
    description: "Fonderie d’art, bronze, moules, coulage, patine, coulage: Fonderie d’art, à Vonnas.",
    headline: "Fonderie d’art, bronze, moules, coulage, patine, coulage: Fonderie d’art, à Vonnas.",
    titleLines: ["Fonderie d’art,", "bronze, moules, coulage,", "patine, coulage : Fonderie d’art,", "à Vonnas"],
    lead: "La Fonderie du Péroux est un atelier spécialisé dans la fonderie d’art à la cire perdue et au sable. Elle travaille différents métaux, notamment le bronze, le laiton et l’aluminium, pour donner forme aux projets des artistes et des sculpteurs.",
    image: {
      src: "/images/fdp-vague.webp",
      alt: "sculpture bronze fonderie du Péroux",
      caption: "Fonderie du Péroux — sculpture en bronze, fonderie d’art à Vonnas",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Fondeur d’art",
    organization: {
      "@type": "Organization",
      name: "Fonderie du Péroux",
      address: { "@type": "PostalAddress", addressLocality: "Vonnas", postalCode: "01540", addressCountry: "FR" },
    },
    ogTitle: "Fonderie d’art, bronze, moules, coulage, patine, coulage",
    sections: [
      {
        h2: "Un atelier de fonderie d’art à Vonnas",
        paragraphs: [
          "La Fonderie du Péroux est un atelier spécialisé dans la fonderie d’art à la cire perdue et au sable. Elle travaille différents métaux, notamment le bronze, le laiton et l’aluminium, pour donner forme aux projets des artistes et des sculpteurs.",
          "De la conception à la réalisation, l’atelier met son savoir-faire technique au service de la création artistique, de la fabrication de pièces originales et de la reproduction de sculptures.",
        ],
      },
      {
        h2: "La fonderie d’art au service de la sculpture",
        paragraphs: [
          "La fonderie d’art occupe une place essentielle dans la réalisation d’une sculpture. Elle permet de transposer une œuvre originale dans le métal, tout en préservant ses volumes, ses détails, ses textures et l'intention artistique du sculpteur.",
          "Grâce aux techniques de fonte à la cire perdue et au sable, la Fonderie du Péroux accompagne les artistes dans la réalisation de sculptures en bronze, en laiton ou en aluminium, depuis la préparation du modèle jusqu'à l'obtention de la pièce fondue.",
        ],
      },
      {
        h2: "Stages et formations aux techniques de fonderie d’art",
        paragraphs: [
          "La transmission des savoir-faire fait également partie des activités de l'atelier. La Fonderie du Péroux propose des stages et des formations permettant de découvrir les différentes étapes de la fonderie d’art, de comprendre les techniques de moulage et de fonte et de se familiariser avec le travail du métal.",
          "Ces formations s'adressent aux artistes, aux sculpteurs et aux personnes désireuses de découvrir les pratiques traditionnelles de la fonderie.",
        ],
      },
      {
        h2: "Conception et réalisation d’ateliers de fonderie d’art",
        paragraphs: [
          "Au-delà de la production de sculptures, l’atelier apporte son expertise dans la conception et la réalisation de projets d’ateliers de fonderie d’art.",
          "Il intervient dans l'étude des besoins techniques, le choix des équipements et l'organisation des installations nécessaires à la pratique de la fonte artistique.",
        ],
      },
    ],
    contactH2: "Contacter la Fonderie du Péroux",
    cityLabel: "Adresse",
    city: "2075 Rte de Péroux, 01290 Biziat",
    address: "",
    tel: "06 32 79 71 56",
    telHref: "+33632797156",
    email: "fonderieduperoux@gmail.com",
    siteUrl: "",
    siteLabel: "",
    images: FONDERIE_IMAGES,
  },
  "petite-barat-polyester": {
    metier: "resine",
    title: "Atelier de Polyester, moules, tirage, patine | Sculpteur Français",
    description: "Atelier de Polyester, moules, tirage, patine Montaigut en Combraille",
    headline: "Atelier de Polyester, moules, tirage, patine",
    titleLines: ["Atelier de Polyester,", "moules, tirage, patine"],
    lead: "Petit-Barat Polyester est un atelier spécialisé au service des sculpteurs souhaitant faire fabriquer des moules en résine et silicone de leurs œuvres ou objets d'art, afin de réaliser des tirages en séries limitées d'une fidélité irréprochable.",
    image: {
      src: "/images/petitbarat-logo.webp",
      alt: "Logo Petit-Barat Polyester, atelier de moulage d'art contemporain",
      caption: "Petit-Barat Polyester — atelier de moulage d'art contemporain",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Résine",
    organization: {
      "@type": "Organization",
      name: "Petit-Barat Polyester",
      url: "https://petitbaratpolyester.com/",
      address: { "@type": "PostalAddress", streetAddress: "Puy Gilard", addressLocality: "Montaigut-en-Combraille", postalCode: "63700", addressCountry: "FR" },
    },
    ogTitle: "Atelier de Polyester, moules, tirage, patine",
    sections: [
      {
        h2: "Au service des artistes sculpteurs",
        paragraphs: [
          "Petit-Barat Polyester est un atelier spécialisé au service des sculpteurs souhaitant faire fabriquer des moules en résine et silicone de leurs œuvres ou objets d'art, afin de réaliser des tirages en séries limitées d'une fidélité irréprochable.",
          "Idéalement implanté au centre de la France à Montaigut-en-Combraille, l'atelier répond à toute demande de fabrication de moule, que vous soyez installé à Paris, Lyon, Nantes, Strasbourg, Bordeaux ou Marseille.",
          "L'atelier collabore depuis de nombreuses années avec des artistes exigeants qui recherchent précision du geste, soin du détail et respect absolu de la pièce matrice.",
        ],
      },
      {
        h2: "Techniques de moulage et façonnage d'art",
        techniques: [
          {
            num: "01",
            title: "Moules Résine & Élastomère",
            text: "Conception de moules en silicone élastomère avec chapes de maintien rigides en résine armée. Idéal pour capter les détails les plus subtils sans contrainte de dépouille.",
            bullets: ["Silicone haute résistance au déchirement", "Chapes démontables, légères et indéformables", "Protection intégrale de votre modèle"],
          },
          {
            num: "02",
            title: "Modelage & Maquettage",
            text: "Préparation de la pièce originale, montage sur armature, étude méthodique des plans de joint pour préserver les lignes esthétiques majeures de votre sculpture.",
            bullets: ["Étude des dépouilles et contre-dépouilles", "Prise en charge tous matériaux (terre, plâtre, cire, bois)", "Accompagnement sur les grandes dimensions"],
          },
          {
            num: "03",
            title: "Tirages en Séries Limitées",
            text: "Fabrication de tirages numérotés en résine polyester ou polyuréthane technique : teintes dans la masse, inclusions minérales (marbre, bronze, ardoise) et lustrage soigné.",
            bullets: ["Résines polyester techniques haute longévité", "Charges minérales et métalliques variées", "Ponçage fin, lustrage et contrôle qualité"],
          },
        ],
      },
    ],
    contactH2: "Contacter Petit-Barat Polyester",
    cityLabel: "Adresse",
    city: "Puy Gilard 63700 Montaigut-en-Combraille",
    address: "",
    tel: "06 10 53 86 01",
    telHref: "+33610538601",
    email: "petit.barat@laposte.net",
    siteUrl: "https://petitbaratpolyester.com/",
    siteLabel: "petitbaratpolyester.com",
    images: PETITBARAT_IMAGES,
  },
  "evoludis-concept": {
    metier: "resine",
    title: "Antoine Odent — Evoludis Concept, fabrication résine | Sculpteur Français",
    description: "Antoine Odent (Evoludis Concept, Portugal) : moulage, tirage en série et finitions peintes de sculptures en résine, pour artistes et professionnels.",
    headline: "Antoine Odent — Evoludis Concept, fabrication résine",
    titleLines: ["Antoine Odent — Evoludis Concept,", "fabrication résine"],
    lead: "Spécialiste du moulage et du tirage en résine, Antoine Odent réalise séries et finitions peintes pour des sculpteurs et des professionnels, de l'atelier portugais à l'œuvre.",
    image: {
      src: "/images/evoludis-logo.webp",
      alt: "Sculpture cavalier d'échecs en résine, Evoludis Concept",
      caption: "Evoludis Concept — fabrication résine, Vila Nova de Famalicão",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Résine",
    organization: {
      "@type": "Organization",
      name: "Evoludis Concept",
      address: { "@type": "PostalAddress", streetAddress: "Rua do Senhor dos Perdões, nº14", addressLocality: "Vila Nova de Famalicão", addressCountry: "PT" },
    },
    ogTitle: "Antoine Odent — Evoludis Concept, fabrication résine",
    sections: [
      {
        h2: "Moulage et tirage en résine",
        paragraphs: [
          "Basé au Portugal, Antoine Odent dirige Evoludis Concept, spécialisé dans le moulage, le tirage et les finitions (peinture et décor) de pièces en résine.",
          "Son atelier prend en charge l'ensemble de la fabrication : réalisation des moules d'après le modèle du sculpteur, tirages en série soignés, puis finitions chromées ou laquées, jusqu'à la pièce prête à présenter.",
          "Evoludis Concept accompagne aussi d'autres artistes et professionnels dans leurs projets de fabrication en résine.",
        ],
      },
    ],
    contactH2: "Contacter Antoine Odent",
    cityLabel: "Adresse",
    city: "Rua do Senhor dos Perdões, nº14, 4760-727 Ribeirão — Vila Nova de Famalicão, Portugal",
    address: "",
    tel: "+351 925 160 794",
    telHref: "+351925160794",
    email: "antoine.odent@evoludis.pt",
    images: EVOLUDIS_IMAGES,
  },
  "art-de-forge": {
    metier: "ferronnerie",
    title: "Jérôme Mazoyer — Art de Forge, ferronnerie d'art | Sculpteur Français",
    description: "Jérôme Mazoyer (Art de Forge, Valréas) : ferronnerie d'art et fabrication métallique sur mesure — forge, soudure, métal découpé, pièces uniques et ouvrages sculpturaux.",
    headline: "Jérôme Mazoyer — Art de Forge, ferronnerie d'art",
    titleLines: ["Jérôme Mazoyer — Art de Forge,", "ferronnerie d'art"],
    lead: "Ferronnier d'art installé à Valréas, Jérôme Mazoyer forge des pièces métalliques sur mesure — sculpture, mobilier, ouvrages d'architecture — avec la rigueur de la forge traditionnelle.",
    image: {
      src: "/images/ferronnerie-logo.webp",
      alt: "Arbre sculptural en métal forgé, atelier Art de Forge",
      caption: "Arbre sculptural en métal forgé — atelier Art de Forge, Valréas",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Ferronnerie",
    organization: {
      "@type": "Organization",
      name: "Art de Forge",
      address: { "@type": "PostalAddress", streetAddress: "Z.I. de la Grèze — Allée Germain Aubert", addressLocality: "Valréas", postalCode: "84600", addressCountry: "FR" },
    },
    ogTitle: "Jérôme Mazoyer — Art de Forge, ferronnerie d'art",
    sections: [
      {
        h2: "Ferronnerie d'art et fabrication sur mesure",
        paragraphs: [
          "Installé à Valréas, dans le Vaucluse, Jérôme Mazoyer dirige l'atelier Art de Forge, spécialisé dans la ferronnerie d'art et la fabrication métallique sur mesure : forge, soudure, métal découpé, pièces uniques.",
          "L'atelier accompagne aussi des projets sculpturaux : structure interne, assemblage, soudure et finitions du métal, de la pièce intime à l'œuvre monumentale. Un beau terrain de rencontre entre la rigueur du travail du fer et la liberté de la sculpture contemporaine.",
          "Au-delà des pièces sur mesure, Art de Forge réalise aussi portails, rampes et ouvrages de ferronnerie classique pour particuliers et professionnels.",
        ],
      },
    ],
    contactH2: "Contacter Jérôme Mazoyer",
    cityLabel: "Adresse",
    city: "Z.I. de la Grèze — Allée Germain Aubert, 84600 Valréas",
    address: "",
    tel: "06 80 70 14 56",
    telHref: "+33680701456",
    email: "art-de-forge@orange.fr",
    images: FERRONNERIE_IMAGES,
  },
};

export default function PartenaireDetailPage() {
  const { metier: metierSlug, partner: partnerId } = useParams();
  const metier = METIERS.find((m) => m.slug === metierSlug);
  const partner = metier?.partners.find((p) => p.id === partnerId && p.detailPage);
  const fiche = FICHES[partnerId];

  useSEO(
    fiche ? fiche.title : "Partenaire — Sculpteur Français",
    fiche ? fiche.description : "Fiche partenaire de l'annuaire des métiers d'art.",
    fiche
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: fiche.headline,
          inLanguage: "fr-FR",
          mainEntityOfPage: `${BASE}/partenaires/${fiche.metier}/${partnerId}`,
          about: fiche.organization,
          author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
          publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
        }
      : null,
    fiche
      ? {
          type: "article",
          ogTitle: fiche.ogTitle,
          ogDescription: fiche.description,
          breadcrumb: [
            { name: "Partenaires", path: "/partenaires-metiers-d-art" },
            { name: fiche.breadcrumbMetier, path: `/partenaires/${fiche.metier}` },
            { name: partner.name, path: `/partenaires/${fiche.metier}/${partnerId}` },
          ],
        }
      : { noindex: true }
  );

  if (!partner || !fiche) return <NotFound />;

  return (
    <ArticleShell
      testId="partenaire-fiche-page"
      eyebrow={`Partenaires — ${metier.name}`}
      titleLines={fiche.titleLines}
      lead={fiche.lead}
      image={fiche.image}
    >
      <Link
        to={`/partenaires/${fiche.metier}`}
        data-testid="fiche-back-link"
        className="group inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
      >
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
        <span className="link-underline">{fiche.breadcrumbMetier}</span>
      </Link>

      {fiche.sections.map((s) => (
        <section key={s.h2}>
          <H2>{s.h2}</H2>
          {s.paragraphs && s.paragraphs.map((t, i) => (
            <P key={i}>{t}</P>
          ))}
          {s.techniques && (
            <div className="mt-8 space-y-8">
              {s.techniques.map((t) => (
                <div key={t.num} className="border-l-2 border-bronze pl-6" data-testid={`technique-${t.num}`}>
                  <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-bronze">{t.num}</p>
                  <h3 className="mt-2 font-serif text-2xl font-normal text-ink">{t.title}</h3>
                  <P>{t.text}</P>
                  <ul className="mt-4 space-y-2">
                    {t.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-base font-light text-body">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-bronze" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}

      <div className="my-10 space-y-10">
        {fiche.images.map((img, i) => (
          <Reveal key={img.src}>
            <figure>
              <div className="fiche-photo-frame">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  data-testid={`fiche-photo-${i + 1}`}
                />
              </div>
            </figure>
          </Reveal>
        ))}
      </div>

      <H2>{fiche.contactH2}</H2>
      <div className="mt-8 border border-ink/15 px-6 py-5" data-testid="fiche-contact">
        <p className="flex items-center gap-2 text-base font-light text-body sm:text-lg">
          <MapPin className="h-4 w-4 shrink-0 text-bronze" />
          {fiche.cityLabel || "Ville"} : {fiche.city}
        </p>
        {fiche.address && (
          <p className="mt-2 flex items-start gap-2 text-base font-light text-body sm:text-lg">
            <MapPin className="h-4 w-4 shrink-0 text-bronze" />
            {fiche.address}
          </p>
        )}
        {fiche.tel && (
          <p className="mt-3 flex items-center gap-2 text-base font-light text-body sm:text-lg">
            <Phone className="h-4 w-4 shrink-0 text-bronze" />
            Téléphone :{" "}
            <a href={`tel:${fiche.telHref}`} className="link-underline text-bronze transition-colors hover:text-ink">
              {fiche.tel}
            </a>
          </p>
        )}
        {fiche.email && (
          <p className="mt-3 flex items-center gap-2 text-base font-light text-body sm:text-lg">
            <Mail className="h-4 w-4 shrink-0 text-bronze" />
            E-mail :{" "}
            <a href={`mailto:${fiche.email}`} className="link-underline text-bronze transition-colors hover:text-ink">
              {fiche.email}
            </a>
          </p>
        )}
        {(fiche.siteUrl || fiche.siteLabel === "") && fiche.siteLabel !== undefined && (
          <p className="mt-3 flex items-center gap-2 text-base font-light text-body sm:text-lg">
            <ArrowUpRight className="h-4 w-4 shrink-0 text-bronze" />
            Site internet :{" "}
            {fiche.siteUrl ? (
              <a
                href={fiche.siteUrl}
                target="_blank"
                rel="noopener"
                data-testid="fiche-site-link"
                className="link-underline text-bronze transition-colors hover:text-ink"
              >
                {fiche.siteLabel}
              </a>
            ) : null}
          </p>
        )}
      </div>

      <SeeAlso
        links={[
          { to: `/partenaires/${fiche.metier}`, label: fiche.breadcrumbMetier },
          { to: "/partenaires-metiers-d-art", label: "Tous les métiers partenaires" },
          { to: "/devenez-partenaire-metiers-d-art", label: "Inscription gratuite à l'annuaire" },
        ]}
      />
    </ArticleShell>
  );
}
